import React, { useCallback, useState } from "react"
import styled from "styled-components"
import { ChromePicker, ColorResult } from "react-color"
import { MdAdd } from "react-icons/md"


type TraceColorPickerProps = {
	selectedColor: string
	onChange: (color: string) => void
	onClose?: () => void
}

const CLINICAL_COLORS: string[][] = [
	["#00CC00", "#FF0000", "#00D4FF", "#FFD700", "#FF44FF", "#4169E1", "#FF8C00", "#FFA500", "#A0A0A0"],
	["#8B00FF", "#00FF7F"],
]

const COLORS = [
	["#E53935", "#EF5350", "#F44336", "#D32F2F", "#C62828", "#B71C1C", "#EC407A", "#E91E63", "#D81B60", "#C2185B"],
	["#AD1457", "#880E4F", "#AB47BC", "#9C27B0", "#8E24AA", "#7B1FA2", "#42A5F5", "#2196F3", "#1E88E5", "#1976D2"],
	["#1565C0", "#207DEA", "#26C6DA", "#00BCD4", "#00ACC1", "#0097A7", "#00838F", "#006064", "#66BB6A", "#4CAF50"],
	["#43A047", "#388E3C", "#2E7D32", "#1B5E20", "#9CCC65", "#8BC34A", "#7CB342", "#689F38", "#558B2F", "#33691E"],
	["#FFA726", "#FF9800", "#FB8C00", "#F57C00", "#EF6C00", "#E65100", "#BCAAA4", "#A1887F", "#8D6E63", "#795548"],
	["#6D4C41", "#A0522D", "#B0BEC5", "#90A4AE", "#78909C", "#607D8B"],
]

export const TraceColorPicker: React.FC<TraceColorPickerProps> = ({ selectedColor, onChange, onClose }) => {
	const [showCustomPicker, setShowCustomPicker] = useState(false)
	const [customColor, setCustomColor] = useState(selectedColor)

	const handleColorClick = useCallback((color: string) => {
		onChange(color)
		onClose?.()
	}, [onChange, onClose])

	const handleCustomColorChange = useCallback((color: ColorResult) => {
		setCustomColor(color.hex)
	}, [])

	const handleCustomColorApply = useCallback(() => {
		onChange(customColor)
		onClose?.()
	}, [customColor, onChange, onClose])

	const lastRow = COLORS[COLORS.length - 1]
	const allRowsExceptLast = COLORS.slice(0, -1)

	return (
		<Container>
			<Grid>
				{CLINICAL_COLORS.map((row, i) => (
					<SwatchRow key={i}>
						{row.map(color => (
							<Swatch
								key={color}
								color={color}
								isSelected={selectedColor.toLowerCase() === color.toLowerCase()}
								onClick={() => handleColorClick(color)}
							/>
						))}
					</SwatchRow>
				))}
			</Grid>

			<Divider />
			<Grid>
				{allRowsExceptLast.map((row, i) => (
					<SwatchRow key={i}>
						{row.map(color => (
							<Swatch
								key={color}
								color={color}
								isSelected={selectedColor.toLowerCase() === color.toLowerCase()}
								onClick={() => handleColorClick(color)}
							/>
						))}
					</SwatchRow>
				))}
				<SwatchRow>
					{lastRow.map(color => (
						<Swatch
							key={color}
							color={color}
							isSelected={selectedColor.toLowerCase() === color.toLowerCase()}
							onClick={() => handleColorClick(color)}
						/>
					))}
					<AddSwatch
						onClick={() => setShowCustomPicker(prev => !prev)}
						isActive={showCustomPicker}
					>
						<MdAdd size={16} />
					</AddSwatch>
				</SwatchRow>
			</Grid>

			{showCustomPicker && (
				<CustomPickerWrapper>
					<ChromePicker
						color={customColor}
						onChange={handleCustomColorChange}
						disableAlpha
					/>
					<ApplyButton onClick={handleCustomColorApply}>
						Apply
					</ApplyButton>
				</CustomPickerWrapper>
			)}
		</Container>
	)
}

const Container = styled.div`
	position: relative;
	background: var(--color-surface-menu);
	border: 1px solid var(--color-border-default);
	border-radius: 8px;
	padding: 16px;
	box-shadow: var(--modal-box-shadow);
	width: fit-content;
`

const Divider = styled.hr`
	border: none;
	border-top: 1px solid var(--color-border-default);
	margin: 12px 0;
`

const Grid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`

const SwatchRow = styled.div`
	display: flex;
	gap: 6px;
	align-items: center;
`

const Swatch = styled.div<{ color: string; isSelected: boolean }>`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background-color: ${({ color }) => color};
	cursor: pointer;
	border: 2px solid ${({ isSelected }) => isSelected ? "var(--color-accent-interactive)" : "transparent"};
	outline: ${({ isSelected }) => isSelected ? "2px solid var(--color-accent-interactive)" : "none"};
	outline-offset: 1px;
	transition: transform 0.1s ease, border-color 0.1s ease;

	&:hover {
		transform: scale(1.2);
	}
`

const AddSwatch = styled.div<{ isActive: boolean }>`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	cursor: pointer;
	border: 2px solid ${({ isActive }) => isActive ? "var(--color-accent-interactive)" : "var(--text-secondary-color)"};
	background: ${({ isActive }) => isActive ? "var(--color-hover-neutral)" : "var(--color-hover-neutral)"};
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ isActive }) => isActive ? "var(--color-accent-interactive)" : "var(--color-text-primary)"};
	transition: transform 0.1s ease, border-color 0.15s ease, color 0.15s ease;

	&:hover {
		transform: scale(1.2);
		border-color: var(--color-accent-interactive);
		color: var(--color-accent-interactive);
	}
`

const CustomPickerWrapper = styled.div`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: calc(100% + 8px);
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	background: var(--color-surface-menu);
	border: 1px solid var(--color-border-default);
	border-radius: 8px;
	padding: 16px;
	box-shadow: var(--modal-box-shadow);
	z-index: 10;

	.chrome-picker {
		background: var(--color-surface-menu) !important;
		box-shadow: none !important;
		border: none !important;
		border-radius: 6px !important;
		width: 225px !important;

		input {
			background: var(--color-input-bg) !important;
			color: var(--color-text-primary) !important;
			border-color: var(--color-border-default) !important;
		}

		label {
			color: var(--text-secondary-color) !important;
		}

		svg {
			fill: var(--text-secondary-color) !important;
		}
	}
`

const ApplyButton = styled.button`
	width: 100%;
	padding: 6px 16px;
	border: none;
	border-radius: 6px;
	background: var(--color-accent-interactive);
	color: var(--color-hover-active-blue-text, white);
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.15s ease;

	&:hover {
		opacity: 0.85;
	}
`
